import React, { useState, useEffect, useRef } from "react";
import Sidebar from "../components/Sidebar";
import { API_BASE_URL } from "../config";
import { Plus, Edit2, Trash2, X, Info, UploadCloud, CheckSquare, Square, Check } from "lucide-react";
import Swal from "sweetalert2";
import axios from "axios";
import { compressFilesBatch } from "../utils/imageCompressor";

const MAX_FILE_SIZE_MB = 500; // 500MB per file limit
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_BATCH_SIZE_MB = 1500; // 1500MB total batch upload limit
const MAX_BATCH_SIZE_BYTES = MAX_BATCH_SIZE_MB * 1024 * 1024;

const formatBytes = (bytes, decimals = 1) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const getTodayIst = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

export default function FestivalCalendar() {
    const [festivals, setFestivals] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState("All");

    // Bulk selection state
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedFestivalIds, setSelectedFestivalIds] = useState([]);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);

    const monthsList = ["All", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

    const [showModal, setShowModal] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedFestivalForView, setSelectedFestivalForView] = useState(null);
    const [currentFest, setCurrentFest] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        festival_name: "",
        festival_date: "",
        is_main_day: false,
        is_quick: false,
        festival_image_hindi: null,
        festival_thumbnail_hindi: null,
        festival_image_english: null,
        festival_thumbnail_english: null
    });

    const fileInputRef = useRef(null);
    const [replaceImageName, setReplaceImageName] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadedBytesInfo, setUploadedBytesInfo] = useState({ loaded: 0, total: 0 });
    const [compressing, setCompressing] = useState(false);
    const [compressionProgress, setCompressionProgress] = useState(0);
    const [compressionStats, setCompressionStats] = useState(null);

    useEffect(() => {
        fetchFestivals();
        fetchLanguages();
    }, []);

    const fetchLanguages = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/languages/all`);
            const data = await res.json();
            if (data.status && data.data) {
                setLanguages(data.data);
            }
        } catch (err) {
            console.error("Error fetching languages:", err);
        }
    };

    const handleViewImages = (fest) => {
        setSelectedFestivalForView(fest);
        setViewModalOpen(true);
    };

    const handleDeleteImage = async (imageName) => {
        if (window.confirm("Are you sure you want to delete this image/video?")) {
            try {
                const response = await fetch(`${API_BASE_URL}/api/festival-calendar/delete-image/${selectedFestivalForView.festival_id_PK}`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ imageName: imageName.split('/').pop() }),
                });

                if (response.ok) {
                    alert("Image deleted successfully");
                    fetchFestivals();
                    const updatedImages = (selectedFestivalForView.festival_images || []).filter(img => !img.includes(imageName.split('/').pop()));
                    const updatedVideos = (selectedFestivalForView.festival_videos || []).filter(img => !img.includes(imageName.split('/').pop()));
                    setSelectedFestivalForView({ ...selectedFestivalForView, festival_images: updatedImages, festival_videos: updatedVideos });
                } else {
                    alert("Failed to delete image");
                }
            } catch (error) {
                console.error("Error deleting image:", error);
                alert("Error deleting image");
            }
        }
    };

    const triggerReplaceImage = (imageName) => {
        setReplaceImageName(imageName.split('/').pop());
        fileInputRef.current.click();
    };

    const handleReplaceFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file || !replaceImageName) return;

        const payload = new FormData();
        payload.append("oldImageName", replaceImageName);
        payload.append("new_image", file);

        Swal.fire({
            title: "Replacing Image...",
            html: `
                <div style="width: 100%; margin-top: 10px; text-align: left;">
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                        <span id="swal-upload-status" style="font-size: 13px; font-weight: 600; color: #475569;">Uploading replacement...</span>
                        <span id="swal-upload-pct" style="font-size: 16px; font-weight: 800; color: #2563eb;">0%</span>
                    </div>
                    <div style="width: 100%; background: #e2e8f0; height: 14px; border-radius: 9999px; overflow: hidden; position: relative;">
                        <div id="swal-progress-bar" style="width: 0%; height: 100%; background: linear-gradient(90deg, #3b82f6, #06b6d4); border-radius: 9999px; transition: width 0.15s ease;"></div>
                    </div>
                    <div style="margin-top: 8px; font-size: 12px; color: #64748b; display: flex; justify-content: space-between;">
                        <span id="swal-upload-bytes">0 B / ${formatBytes(file.size)}</span>
                        <span style="max-width: 150px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${file.name}</span>
                    </div>
                </div>
            `,
            allowOutsideClick: false,
            showConfirmButton: false
        });

        try {
            const response = await axios.put(
                `${API_BASE_URL}/api/festival-calendar/replace-image/${selectedFestivalForView.festival_id_PK}`,
                payload,
                {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (progressEvent) => {
                        if (progressEvent.total) {
                            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                            const pctEl = document.getElementById("swal-upload-pct");
                            const barEl = document.getElementById("swal-progress-bar");
                            const bytesEl = document.getElementById("swal-upload-bytes");
                            const statusEl = document.getElementById("swal-upload-status");

                            if (pctEl) pctEl.innerText = `${percent}%`;
                            if (barEl) barEl.style.width = `${percent}%`;
                            if (bytesEl) bytesEl.innerText = `${formatBytes(progressEvent.loaded)} / ${formatBytes(progressEvent.total)}`;
                            if (statusEl && percent >= 100) {
                                statusEl.innerText = "Processing replacement on server...";
                            }
                        }
                    }
                }
            );

            Swal.close();
            if (response.data && response.data.status) {
                Swal.fire({
                    icon: "success",
                    title: "Replaced!",
                    text: "Image replaced successfully",
                    timer: 1500,
                    showConfirmButton: false
                });
                fetchFestivals();
                setViewModalOpen(false);
            } else {
                Swal.fire("Error", response.data?.message || "Failed to replace image", "error");
            }
        } catch (error) {
            Swal.close();
            console.error("Error replacing image:", error);
            Swal.fire("Error", error.response?.data?.message || error.message || "Error replacing image", "error");
        }
        e.target.value = null; // Reset input
    };

    const fetchFestivals = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/festival-calendar/all`);
            const data = await res.json();
            if (data.status && data.data) {
                const normalized = data.data.map(fest => {
                    const hindi = fest.Hindi || {};
                    const english = fest.English || {};
                    return {
                        ...fest,
                        festival_images: [...(hindi.festival_images || []), ...(english.festival_images || [])],
                        festival_videos: [...(hindi.festival_videos || []), ...(english.festival_videos || [])],
                        festival_thumbnail: hindi.festival_thumbnail || english.festival_thumbnail,
                        language: fest.Hindi && fest.English ? 'Both' : (fest.Hindi ? 'Hindi' : (fest.English ? 'English' : null))
                    };
                });
                setFestivals(normalized);
            }
        } catch (err) {
            console.error("Error fetching festivals:", err);
        }
    };

    const handleOpenModal = (fest = null) => {
        if (fest) {
            setEditingId(fest.festival_id_PK);
            setFormData({
                festival_name: fest.festival_name,
                festival_date: fest.festival_date ? String(fest.festival_date).split('T')[0] : getTodayIst(),
                is_main_day: fest.is_main_day === 1,
                is_quick: fest.is_quick === 1,
                festival_image_hindi: null,
                festival_thumbnail_hindi: null,
                festival_image_english: null,
                festival_thumbnail_english: null
            });
        } else {
            setEditingId(null);
            setFormData({
                festival_name: "",
                festival_date: getTodayIst(),
                is_main_day: false,
                is_quick: false,
                festival_image_hindi: null,
                festival_thumbnail_hindi: null,
                festival_image_english: null,
                festival_thumbnail_english: null
            });
        }
        setShowModal(true);
    };

    // Calculate total media bytes
    const hindiImagesBytes = formData.festival_image_hindi ? Array.from(formData.festival_image_hindi).reduce((acc, f) => acc + (f.size || 0), 0) : 0;
    const hindiThumbBytes = formData.festival_thumbnail_hindi?.size || 0;
    const englishImagesBytes = formData.festival_image_english ? Array.from(formData.festival_image_english).reduce((acc, f) => acc + (f.size || 0), 0) : 0;
    const englishThumbBytes = formData.festival_thumbnail_english?.size || 0;
    const grandTotalBytes = hindiImagesBytes + hindiThumbBytes + englishImagesBytes + englishThumbBytes;

    const handleFilesSelect = (e, fieldName) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        for (let i = 0; i < files.length; i++) {
            if (files[i].size > MAX_FILE_SIZE_BYTES) {
                Swal.fire({
                    icon: "error",
                    title: "File Too Large",
                    text: `"${files[i].name}" (${formatBytes(files[i].size)}) exceeds maximum allowed limit of ${MAX_FILE_SIZE_MB} MB.`
                });
                e.target.value = "";
                return;
            }
        }
        setFormData(prev => ({ ...prev, [fieldName]: files }));
    };

    const handleSingleFileSelect = (e, fieldName) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > MAX_FILE_SIZE_BYTES) {
            Swal.fire({
                icon: "error",
                title: "File Too Large",
                text: `"${file.name}" (${formatBytes(file.size)}) exceeds maximum allowed limit of ${MAX_FILE_SIZE_MB} MB.`
            });
            e.target.value = "";
            return;
        }
        setFormData(prev => ({ ...prev, [fieldName]: file }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (grandTotalBytes > MAX_BATCH_SIZE_BYTES) {
            Swal.fire({
                icon: "error",
                title: "Batch Limit Exceeded",
                text: `Total upload size (${formatBytes(grandTotalBytes)}) exceeds the maximum batch limit of ${MAX_BATCH_SIZE_MB} MB.`
            });
            return;
        }

        setLoading(true);
        setUploadProgress(0);
        setCompressionStats(null);
        setCompressing(true);
        setCompressionProgress(0);

        // Collect files to compress
        const hindiFiles = formData.festival_image_hindi ? Array.from(formData.festival_image_hindi) : [];
        const englishFiles = formData.festival_image_english ? Array.from(formData.festival_image_english) : [];
        const hindiThumb = formData.festival_thumbnail_hindi;
        const englishThumb = formData.festival_thumbnail_english;

        const itemsToCompress = [
            ...hindiFiles.map(f => ({ file: f, category: 'hindi_img' })),
            ...englishFiles.map(f => ({ file: f, category: 'english_img' })),
            ...(hindiThumb ? [{ file: hindiThumb, category: 'hindi_thumb' }] : []),
            ...(englishThumb ? [{ file: englishThumb, category: 'english_thumb' }] : [])
        ];

        let finalItems = itemsToCompress;
        let compStats = null;
        if (itemsToCompress.length > 0) {
            const result = await compressFilesBatch(itemsToCompress, (pct) => {
                setCompressionProgress(pct);
            });
            finalItems = result.compressedItems;
            compStats = result.stats;
            setCompressionStats(result.stats);
        }
        setCompressing(false);

        const effectiveTotal = compStats?.compressedTotal || grandTotalBytes;
        setUploadedBytesInfo({ loaded: 0, total: effectiveTotal });

        const savedStatsBadge = (compStats && compStats.savedPercent > 0)
            ? `<div style="margin-top: 8px; padding-top: 6px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #059669; font-weight: 600; display: flex; justify-content: space-between;"><span>⚡ Compressed: <span style="text-decoration: line-through; color: #94a3b8;">${formatBytes(compStats.originalTotal)}</span> ➔ ${formatBytes(compStats.compressedTotal)}</span><span style="background: #d1fae5; color: #065f46; padding: 2px 6px; border-radius: 4px;">Saved ${compStats.savedPercent}%</span></div>`
            : '';

        Swal.fire({
            title: editingId ? "Updating Festival..." : "Uploading Festival Media...",
            html: `
                <div style="width: 100%; margin-top: 10px; text-align: left;">
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                        <span id="swal-upload-status" style="font-size: 13px; font-weight: 600; color: #475569;">Uploading files...</span>
                        <span id="swal-upload-pct" style="font-size: 18px; font-weight: 800; color: #2563eb;">0%</span>
                    </div>
                    <div style="width: 100%; background: #e2e8f0; height: 16px; border-radius: 9999px; overflow: hidden; position: relative;">
                        <div id="swal-progress-bar" style="width: 0%; height: 100%; background: linear-gradient(90deg, #3b82f6, #06b6d4); border-radius: 9999px; transition: width 0.15s ease;"></div>
                    </div>
                    <div style="margin-top: 10px; font-size: 12px; color: #64748b; display: flex; justify-content: space-between;">
                        <span id="swal-upload-bytes">0 B / ${formatBytes(effectiveTotal)}</span>
                        <span id="swal-upload-hint">Please keep window open</span>
                    </div>
                    ${savedStatsBadge}
                </div>
            `,
            allowOutsideClick: false,
            showConfirmButton: false
        });

        const formPayload = new FormData();
        formPayload.append("festival_name", formData.festival_name);
        formPayload.append("festival_date", formData.festival_date);
        formPayload.append("is_main_day", formData.is_main_day ? 1 : 0);
        formPayload.append("is_quick", formData.is_quick ? 1 : 0);

        finalItems.forEach(it => {
            if (it.category === 'hindi_img') formPayload.append("festival_image_hindi", it.file);
            else if (it.category === 'english_img') formPayload.append("festival_image_english", it.file);
            else if (it.category === 'hindi_thumb') formPayload.append("festival_thumbnail_hindi", it.file);
            else if (it.category === 'english_thumb') formPayload.append("festival_thumbnail_english", it.file);
        });

        try {
            let url = `${API_BASE_URL}/api/festival-calendar/create`;
            let method = "post";
            if (editingId) {
                url = `${API_BASE_URL}/api/festival-calendar/update/${editingId}`;
                method = "put";
            }

            const response = await axios({
                method,
                url,
                data: formPayload,
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                        const rawPercent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        const scaledPercent = Math.min(90, Math.round((progressEvent.loaded * 90) / progressEvent.total));
                        setUploadProgress(scaledPercent);
                        setUploadedBytesInfo({ loaded: progressEvent.loaded, total: progressEvent.total });

                        const pctEl = document.getElementById("swal-upload-pct");
                        const barEl = document.getElementById("swal-progress-bar");
                        const bytesEl = document.getElementById("swal-upload-bytes");
                        const statusEl = document.getElementById("swal-upload-status");

                        if (bytesEl) bytesEl.innerText = `${formatBytes(progressEvent.loaded)} / ${formatBytes(progressEvent.total)}`;

                        if (rawPercent < 100) {
                            if (pctEl) pctEl.innerText = `${scaledPercent}%`;
                            if (barEl) barEl.style.width = `${scaledPercent}%`;
                            if (statusEl) statusEl.innerText = `Uploading: ${scaledPercent}%...`;
                        } else {
                            if (barEl) {
                                barEl.style.width = '95%';
                                barEl.style.background = 'linear-gradient(90deg, #3b82f6, #06b6d4, #10b981)';
                            }
                            if (pctEl) pctEl.innerText = 'Saving...';
                            if (statusEl) {
                                statusEl.innerHTML = '<span style="display:inline-flex; align-items:center; gap:6px;"><span class="inline-block w-3.5 h-3.5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span> <strong>Optimizing & saving to Cloud...</strong></span>';
                            }
                        }
                    }
                }
            });

            const result = response.data;

            const pctEl = document.getElementById("swal-upload-pct");
            const barEl = document.getElementById("swal-progress-bar");
            const statusEl = document.getElementById("swal-upload-status");
            if (barEl) {
                barEl.style.width = '100%';
                barEl.style.background = '#10b981';
            }
            if (pctEl) pctEl.innerText = '100%';
            if (statusEl) statusEl.innerHTML = '<span style="color:#10b981; font-weight:700;">✅ Upload complete!</span>';
            setUploadProgress(100);

            await new Promise(resolve => setTimeout(resolve, 350));

            Swal.close();
            if (result.status) {
                let successMsg = editingId ? "Festival updated successfully!" : "Festival created successfully!";
                if (compStats && compStats.savedPercent > 0) {
                    successMsg += ` (⚡ Compressed: ${formatBytes(compStats.originalTotal)} ➔ ${formatBytes(compStats.compressedTotal)}, Saved ${compStats.savedPercent}%)`;
                }
                Swal.fire({
                    icon: "success",
                    title: "Success",
                    text: successMsg
                });
                setShowModal(false);
                fetchFestivals();
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: result.message || "Something went wrong!"
                });
            }
        } catch (err) {
            Swal.close();
            console.error(err);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: err.response?.data?.message || err.message || "Failed to submit festival data"
            });
        }
        setCompressing(false);
        setLoading(false);
    };

    const displayedFestivals = festivals.filter(fest => {
        if (selectedMonth === "All") return true;
        const festMonth = new Date(fest.festival_date).toLocaleString('default', { month: 'long' });
        return festMonth === selectedMonth;
    });

    const isAllSelected = displayedFestivals.length > 0 && displayedFestivals.every(f => selectedFestivalIds.includes(f.festival_id_PK));

    const toggleSelectionMode = () => {
        if (isSelectionMode) {
            setIsSelectionMode(false);
            setSelectedFestivalIds([]);
        } else {
            setIsSelectionMode(true);
        }
    };

    const handleToggleSelectFestival = (id, e) => {
        if (e) e.stopPropagation();
        setSelectedFestivalIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const handleSelectAll = () => {
        const currentFilteredIds = displayedFestivals.map(f => f.festival_id_PK);
        if (isAllSelected) {
            setSelectedFestivalIds(prev => prev.filter(id => !currentFilteredIds.includes(id)));
        } else {
            setSelectedFestivalIds(prev => Array.from(new Set([...prev, ...currentFilteredIds])));
        }
    };

    const handleBulkDelete = async () => {
        if (selectedFestivalIds.length === 0) {
            Swal.fire({
                icon: "warning",
                title: "No Selection",
                text: "Please select at least one festival checkbox to delete."
            });
            return;
        }

        const confirmResult = await Swal.fire({
            title: `Delete ${selectedFestivalIds.length} Festival(s)?`,
            text: `Are you sure you want to delete ${selectedFestivalIds.length} selected festival(s)? This action cannot be undone.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ef4444",
            cancelButtonColor: "#64748b",
            confirmButtonText: `Yes, Delete (${selectedFestivalIds.length})`,
            cancelButtonText: "Cancel"
        });

        if (confirmResult.isConfirmed) {
            setIsBulkDeleting(true);
            try {
                const res = await fetch(`${API_BASE_URL}/api/festival-calendar/bulk-delete`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ ids: selectedFestivalIds })
                });
                const data = await res.json();
                setIsBulkDeleting(false);

                if (data.status) {
                    Swal.fire({
                        icon: "success",
                        title: "Deleted!",
                        text: data.message || `${selectedFestivalIds.length} festival(s) deleted successfully.`,
                        timer: 2000,
                        showConfirmButton: false
                    });
                    setSelectedFestivalIds([]);
                    setIsSelectionMode(false);
                    fetchFestivals();
                } else {
                    Swal.fire({
                        icon: "error",
                        title: "Failed to Delete",
                        text: data.message || "Failed to delete selected festivals."
                    });
                }
            } catch (err) {
                setIsBulkDeleting(false);
                console.error("Bulk delete error:", err);
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Something went wrong while deleting festivals."
                });
            }
        }
    };

    const handleDelete = async (id, name) => {
        const result = await Swal.fire({
            title: `Delete "${name || 'Festival'}"?`,
            text: "This festival entry will be permanently deleted.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ef4444",
            cancelButtonColor: "#64748b",
            confirmButtonText: "Yes, delete it!"
        });

        if (result.isConfirmed) {
            try {
                const res = await fetch(`${API_BASE_URL}/api/festival-calendar/delete/${id}`, {
                    method: "DELETE"
                });
                const data = await res.json();
                if (data.status) {
                    Swal.fire({
                        icon: "success",
                        title: "Deleted!",
                        text: "Festival deleted successfully.",
                        timer: 1500,
                        showConfirmButton: false
                    });
                    fetchFestivals();
                } else {
                    Swal.fire("Error", data.message || "Failed to delete festival", "error");
                }
            } catch (err) {
                console.error(err);
                Swal.fire("Error", "Something went wrong while deleting", "error");
            }
        }
    };

    return (
        <div className="flex h-screen bg-slate-50 dark:bg-slate-900">
            <Sidebar isOpen={isSidebarOpen} onClose={() => setSidebarOpen(false)} />

            <div className="flex-1 flex flex-col min-w-0 overflow-hidden lg:pl-64">
                {/* Header */}
                <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 min-h-[64px] py-3 sm:py-0 flex flex-col sm:flex-row items-center justify-between px-4 sm:px-6 gap-3 z-10 sticky top-0">
                    <div className="flex items-center gap-4 w-full sm:w-auto">
                        <button
                            onClick={() => setSidebarOpen(!isSidebarOpen)}
                            className="lg:hidden p-2 -ml-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </button>
                        <h1 className="text-xl font-bold text-slate-800 dark:text-white truncate">Festival Calendar</h1>
                    </div>
                    <div className="flex flex-wrap items-center gap-2.5 w-full sm:w-auto justify-end">
                        {!isSelectionMode ? (
                            <>
                                <button
                                    onClick={toggleSelectionMode}
                                    className="bg-white hover:bg-slate-50 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600 px-3.5 py-2 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 shadow-sm"
                                    title="Select multiple festivals to delete"
                                >
                                    <CheckSquare size={16} className="text-slate-500 dark:text-slate-400" />
                                    <span>Select</span>
                                </button>
                                <button
                                    onClick={() => handleOpenModal()}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2 shadow-sm"
                                >
                                    <Plus size={18} />
                                    <span>Add Festival</span>
                                </button>
                            </>
                        ) : (
                            <div className="flex flex-wrap items-center gap-2 bg-slate-100 dark:bg-slate-700/60 p-1.5 rounded-xl border border-slate-200 dark:border-slate-600">
                                <button
                                    onClick={handleSelectAll}
                                    className="bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 border border-slate-200 dark:border-slate-700 shadow-xs transition-colors hover:bg-slate-50"
                                >
                                    {isAllSelected ? <CheckSquare size={14} className="text-blue-600" /> : <Square size={14} />}
                                    <span>{isAllSelected ? "Deselect All" : "Select All"}</span>
                                </button>
                                <span className="text-xs bg-red-100 dark:bg-red-950/50 text-red-700 dark:text-red-300 px-2.5 py-1 rounded-md font-bold">
                                    {selectedFestivalIds.length} Selected
                                </span>
                                <button
                                    onClick={handleBulkDelete}
                                    disabled={selectedFestivalIds.length === 0 || isBulkDeleting}
                                    className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 shadow-sm"
                                >
                                    {isBulkDeleting ? (
                                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <Trash2 size={14} />
                                    )}
                                    <span>Delete Selected ({selectedFestivalIds.length})</span>
                                </button>
                                <button
                                    onClick={toggleSelectionMode}
                                    className="text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto p-4 sm:p-6 pb-24">
                    {/* Month Filter */}
                    <div className="flex gap-2 overflow-x-auto pb-4 mb-2 hide-scrollbar">
                        {monthsList.map((m, idx) => (
                            <button
                                key={idx}
                                onClick={() => setSelectedMonth(m)}
                                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold transition-colors ${selectedMonth === m
                                    ? 'bg-blue-600 text-white shadow-md'
                                    : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700'
                                    }`}
                            >
                                {m}
                            </button>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {displayedFestivals.map(fest => {
                            const isSelected = selectedFestivalIds.includes(fest.festival_id_PK);
                            return (
                                <div
                                    key={fest.festival_id_PK}
                                    onClick={isSelectionMode ? (e) => handleToggleSelectFestival(fest.festival_id_PK, e) : undefined}
                                    className={`bg-white dark:bg-slate-800 rounded-2xl border shadow-sm overflow-hidden flex flex-col transition-all relative ${
                                        isSelected
                                            ? "ring-2 ring-red-500 border-red-500 shadow-md bg-red-50/10"
                                            : "border-slate-200 dark:border-slate-700"
                                    } ${isSelectionMode ? "cursor-pointer hover:border-red-300" : ""}`}
                                >
                                    {/* Checkbox (shown in selection mode) */}
                                    {isSelectionMode && (
                                        <div
                                            onClick={(e) => handleToggleSelectFestival(fest.festival_id_PK, e)}
                                            className="absolute top-2.5 left-2.5 z-30 cursor-pointer"
                                        >
                                            <div
                                                className={`w-7 h-7 rounded-lg border-2 flex items-center justify-center transition-all shadow-md ${
                                                    isSelected
                                                        ? "bg-red-600 border-red-600 text-white scale-105"
                                                        : "bg-white/95 dark:bg-slate-800/95 border-slate-400 text-transparent hover:border-red-500"
                                                }`}
                                            >
                                                <Check size={16} strokeWidth={3} className={isSelected ? "opacity-100" : "opacity-0"} />
                                            </div>
                                        </div>
                                    )}

                                    <div
                                        className="h-48 bg-slate-100 dark:bg-slate-700 relative cursor-pointer group"
                                        onClick={isSelectionMode ? (e) => handleToggleSelectFestival(fest.festival_id_PK, e) : () => handleViewImages(fest)}
                                    >
                                        {(() => {
                                            const allMedia = [...(fest.festival_images || []), ...(fest.festival_videos || [])];
                                            if (allMedia.length > 0) {
                                                return allMedia[0].match(/\.(mp4|webm|avi|mpeg|quicktime|mov)$/i) ? (
                                                    <video src={allMedia[0]} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" muted loop autoPlay playsInline />
                                                ) : (
                                                    <img src={allMedia[0]} alt={fest.festival_name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                                                );
                                            }
                                            return <div className="flex items-center justify-center h-full text-slate-400">No Image/Video</div>;
                                        })()}
                                        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <span className="bg-white/80 text-black px-3 py-1 rounded-full text-sm font-medium backdrop-blur-sm">View All ({((fest.festival_images?.length || 0) + (fest.festival_videos?.length || 0))})</span>
                                        </div>
                                        {fest.festival_thumbnail && (
                                            <div className="absolute bottom-2 left-2 w-10 h-10 rounded-full border-2 border-white overflow-hidden bg-white">
                                                <img src={fest.festival_thumbnail} alt="Thumbnail" className="w-full h-full object-cover" />
                                            </div>
                                        )}
                                        {fest.is_main_day === 1 && (
                                            <div className="absolute top-2 right-2 bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-1 rounded shadow-sm flex items-center gap-1">
                                                ⭐ Main Day
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-4 flex-1 flex flex-col">
                                        <h3 className="font-semibold text-slate-800 dark:text-white text-lg flex items-center gap-2">
                                            {fest.festival_name}
                                            {fest.language && (
                                                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-md ${fest.language === 'English' ? 'bg-emerald-100/80 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300' : 'bg-blue-100/80 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300'
                                                    }`}>
                                                    {fest.language}
                                                </span>
                                            )}
                                        </h3>
                                        <div className="flex justify-between items-center mt-1 mb-4 flex-1">
                                            <p className="text-slate-500 dark:text-slate-400 text-sm">
                                                {new Date(fest.festival_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                            </p>
                                        </div>
                                        <div className="flex flex-col sm:flex-row gap-2 mt-auto">
                                            <button
                                                onClick={() => handleOpenModal(fest)}
                                                className="flex-1 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 py-2.5 sm:py-2 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                            >
                                                <Edit2 size={16} /> Edit
                                            </button>
                                            <button
                                                onClick={() => handleDelete(fest.festival_id_PK, fest.festival_name)}
                                                className="flex-1 bg-red-50 hover:bg-red-100 dark:bg-red-500/10 dark:hover:bg-red-500/20 text-red-600 dark:text-red-400 py-2.5 sm:py-2 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                            >
                                                <Trash2 size={16} /> Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </main>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4 sm:p-6">
                    <div className="bg-white dark:bg-slate-800 rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden shadow-xl border border-slate-200 dark:border-slate-700">
                        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center shrink-0">
                            <h3 className="text-xl font-bold text-slate-800 dark:text-white">
                                {editingId ? "Edit Festival" : "Add Festival"}
                            </h3>
                            <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-2 -mr-2 rounded-full transition-colors">
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto flex-1">
                            {/* Max Upload Size & Format Information Banner */}
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-800 dark:to-slate-800/80 border border-blue-200/80 dark:border-blue-900/50 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs shadow-sm">
                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-blue-600/10 text-blue-600 dark:text-blue-400 rounded-xl shrink-0 mt-0.5">
                                        <Info size={18} />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-bold text-slate-800 dark:text-slate-100 text-sm">Upload Size Limit:</span>
                                            <span className="bg-blue-600 text-white font-bold px-2 py-0.5 rounded-md text-[11px] shadow-sm">
                                                Max {MAX_FILE_SIZE_MB} MB / file
                                            </span>
                                            <span className="text-slate-500 dark:text-slate-400 font-medium">
                                                (Up to {MAX_BATCH_SIZE_MB} MB total per batch)
                                            </span>
                                        </div>
                                        <p className="text-slate-500 dark:text-slate-400 mt-1">
                                            Supported: <span className="font-semibold text-slate-700 dark:text-slate-300">JPG, PNG, WEBP, GIF, SVG, BMP, MP4, WEBM, MOV</span> (High quality images & videos)
                                        </p>
                                    </div>
                                </div>
                                {grandTotalBytes > 0 && (
                                    <div className="flex items-center gap-2 bg-white dark:bg-slate-900 px-3.5 py-2 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm shrink-0">
                                        <UploadCloud size={16} className="text-blue-600 dark:text-blue-400" />
                                        <div className="text-right">
                                            <span className="text-slate-400 text-[10px] block">Selected Media</span>
                                            <span className={`font-bold text-xs ${grandTotalBytes > MAX_BATCH_SIZE_BYTES ? 'text-red-500' : 'text-blue-600 dark:text-blue-400'}`}>
                                                {formatBytes(grandTotalBytes)}
                                            </span>
                                            <span className="text-slate-400 text-[10px]"> / {MAX_BATCH_SIZE_MB} MB</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Festival Name *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.festival_name}
                                        onChange={(e) => setFormData({ ...formData, festival_name: e.target.value })}
                                        className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-slate-800 dark:text-white transition-all"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Festival Date *</label>
                                    <input
                                        type="date"
                                        required
                                        min={getTodayIst()}
                                        value={formData.festival_date}
                                        onChange={(e) => setFormData({ ...formData, festival_date: e.target.value })}
                                        className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-slate-800 dark:text-white transition-all"
                                    />
                                </div>
                            </div>

                            {/* Dual Image Upload Section */}
                            <div className="space-y-4">
                                {/* Hindi Upload */}
                                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-800/30">
                                    <label className="block text-sm font-semibold text-blue-800 dark:text-blue-300 mb-3">
                                        Upload Hindi Variant
                                    </label>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="file"
                                                id="hindiImages"
                                                multiple
                                                className="hidden"
                                                accept="image/*,video/*,.jpg,.jpeg,.png,.webp,.gif,.svg,.bmp,.mp4,.webm,.mov"
                                                onChange={(e) => handleFilesSelect(e, "festival_image_hindi")}
                                            />
                                            <label
                                                htmlFor="hindiImages"
                                                className="bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-100 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-700 transition-colors"
                                            >
                                                Choose Files
                                            </label>
                                            <span className="text-sm text-slate-500">
                                                {formData.festival_image_hindi ? `${formData.festival_image_hindi.length} files selected` : 'No file chosen'}
                                            </span>
                                        </div>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                            Images/Videos for Hindi language
                                        </p>

                                        <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-800/40">
                                            <label className="block text-sm font-semibold text-blue-800 dark:text-blue-300 mb-2">
                                                Hindi Thumbnail (Optional)
                                            </label>
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="file"
                                                    id="hindiThumbnail"
                                                    className="hidden"
                                                    accept="image/*,.jpg,.jpeg,.png,.webp,.gif,.svg,.bmp"
                                                    onChange={(e) => handleSingleFileSelect(e, "festival_thumbnail_hindi")}
                                                />
                                                <label
                                                    htmlFor="hindiThumbnail"
                                                    className="bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-100 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-700 transition-colors"
                                                >
                                                    Choose File
                                                </label>
                                                <span className="text-sm text-slate-500">
                                                    {formData.festival_thumbnail_hindi ? formData.festival_thumbnail_hindi.name : 'No file chosen'}
                                                </span>
                                            </div>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                                                Preferred Size: 1080x1080
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* English Upload */}
                                <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-xl border border-emerald-100 dark:border-emerald-800/30">
                                    <label className="block text-sm font-semibold text-emerald-800 dark:text-emerald-300 mb-3">
                                        Upload English Variant
                                    </label>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="file"
                                                id="englishImages"
                                                multiple
                                                className="hidden"
                                                accept="image/*,video/*,.jpg,.jpeg,.png,.webp,.gif,.svg,.bmp,.mp4,.webm,.mov"
                                                onChange={(e) => handleFilesSelect(e, "festival_image_english")}
                                            />
                                            <label
                                                htmlFor="englishImages"
                                                className="bg-emerald-100 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-100 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer hover:bg-emerald-200 dark:hover:bg-emerald-700 transition-colors"
                                            >
                                                Choose Files
                                            </label>
                                            <span className="text-sm text-slate-500">
                                                {formData.festival_image_english ? `${formData.festival_image_english.length} files selected` : 'No file chosen'}
                                            </span>
                                        </div>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                            Images/Videos for English language
                                        </p>

                                        <div className="mt-4 pt-4 border-t border-emerald-200 dark:border-emerald-800/40">
                                            <label className="block text-sm font-semibold text-emerald-800 dark:text-emerald-300 mb-2">
                                                English Thumbnail (Optional)
                                            </label>
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="file"
                                                    id="englishThumbnail"
                                                    className="hidden"
                                                    accept="image/*,.jpg,.jpeg,.png,.webp,.gif,.svg,.bmp"
                                                    onChange={(e) => handleSingleFileSelect(e, "festival_thumbnail_english")}
                                                />
                                                <label
                                                    htmlFor="englishThumbnail"
                                                    className="bg-emerald-100 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-100 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer hover:bg-emerald-200 dark:hover:bg-emerald-700 transition-colors"
                                                >
                                                    Choose File
                                                </label>
                                                <span className="text-sm text-slate-500">
                                                    {formData.festival_thumbnail_english ? formData.festival_thumbnail_english.name : 'No file chosen'}
                                                </span>
                                            </div>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                                                Preferred Size: 1080x1080
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-row items-start sm:items-center cursor-pointer" onClick={() => setFormData({ ...formData, is_main_day: !formData.is_main_day })}>
                                <input
                                    type="checkbox"
                                    checked={formData.is_main_day}
                                    onChange={(e) => setFormData({ ...formData, is_main_day: e.target.checked })}
                                    className="w-5 h-5 mt-0.5 sm:mt-0 shrink-0 cursor-pointer text-blue-600 bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded focus:ring-blue-500"
                                />
                                <label className="ml-3 block text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer select-none">
                                    ⭐ Mark as Main Day
                                </label>
                            </div>
                            <div className="flex flex-row items-start sm:items-center cursor-pointer" onClick={() => setFormData({ ...formData, is_quick: !formData.is_quick })}>
                                <input
                                    type="checkbox"
                                    checked={formData.is_quick}
                                    onChange={(e) => setFormData({ ...formData, is_quick: e.target.checked })}
                                    className="w-5 h-5 mt-0.5 sm:mt-0 shrink-0 cursor-pointer text-blue-600 bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded focus:ring-blue-500"
                                />
                                <label className="ml-3 block text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer select-none">
                                    🚀 Enable Quick Festival Assignment
                                </label>
                            </div>
                            <div className="sticky bottom-0 bg-white dark:bg-slate-800 pt-2 pb-1 space-y-2">
                                {loading && (
                                    <div className="bg-blue-50 dark:bg-blue-950/40 p-3 rounded-xl border border-blue-200 dark:border-blue-800 space-y-2 shadow-sm">
                                        <div className="flex items-center justify-between text-xs font-semibold">
                                            <span className="text-blue-700 dark:text-blue-300 flex items-center gap-1.5">
                                                {compressing ? (
                                                    <>
                                                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse" />
                                                        ⚡ Compressing Images: {compressionProgress}%
                                                    </>
                                                ) : uploadProgress >= 100 ? (
                                                    <>
                                                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                                                        Processing on server...
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse" />
                                                        Uploading: {uploadProgress}%
                                                    </>
                                                )}
                                            </span>
                                            <span className={`font-bold text-sm ${compressing ? 'text-amber-600 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400'}`}>
                                                {compressing ? `${compressionProgress}%` : `${uploadProgress}%`}
                                            </span>
                                        </div>
                                        <div className="w-full bg-blue-100 dark:bg-blue-900/50 h-3 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full rounded-full transition-all duration-200 ${
                                                    compressing
                                                        ? 'bg-gradient-to-r from-amber-500 via-orange-400 to-emerald-500'
                                                        : uploadProgress >= 100
                                                        ? 'bg-gradient-to-r from-emerald-500 to-teal-500'
                                                        : 'bg-gradient-to-r from-blue-600 to-cyan-500'
                                                }`}
                                                style={{ width: `${compressing ? compressionProgress : uploadProgress}%` }}
                                            />
                                        </div>
                                        <div className="text-[11px] text-slate-500 dark:text-slate-400 flex justify-between items-center">
                                            {compressing ? (
                                                <>
                                                    <span>Optimizing image resolution & size...</span>
                                                    <span>{formatBytes(grandTotalBytes)} original</span>
                                                </>
                                            ) : (
                                                <>
                                                    <span>{formatBytes(uploadedBytesInfo.loaded)} uploaded</span>
                                                    <span>{formatBytes(uploadedBytesInfo.total || grandTotalBytes)} total</span>
                                                </>
                                            )}
                                        </div>
                                        {compressionStats && compressionStats.savedPercent > 0 && (
                                            <div className="pt-2 border-t border-blue-200/60 dark:border-blue-800/60 flex items-center justify-between text-xs">
                                                <span className="text-emerald-700 dark:text-emerald-400 font-semibold flex items-center gap-1">
                                                    ⚡ Compressed:
                                                    <span className="line-through text-slate-400 font-normal">{formatBytes(compressionStats.originalTotal)}</span>
                                                    <span>➔</span>
                                                    <span className="font-bold">{formatBytes(compressionStats.compressedTotal)}</span>
                                                </span>
                                                <span className="bg-emerald-100 dark:bg-emerald-950/70 text-emerald-800 dark:text-emerald-300 px-2 py-0.5 rounded-full font-bold text-[11px]">
                                                    Saved {compressionStats.savedPercent}% ({formatBytes(compressionStats.savedBytes)})
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-xl transition duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {loading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            <span>
                                                {compressing
                                                    ? `Compressing Images (${compressionProgress}%)...`
                                                    : `Uploading (${uploadProgress}%)...`}
                                            </span>
                                        </>
                                    ) : (
                                        editingId ? "Update Festival" : "Add Festival"
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {/* View Images Modal */}
            {viewModalOpen && selectedFestivalForView && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border-b border-slate-200 dark:border-slate-700 p-6 flex items-center justify-between z-10">
                            <div>
                                <h2 className="text-xl font-semibold text-slate-800 dark:text-white">Images for {selectedFestivalForView.festival_name}</h2>
                                <p className="text-sm text-slate-500">Date: {new Date(selectedFestivalForView.festival_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                            </div>
                            <button
                                onClick={() => {
                                    setViewModalOpen(false);
                                    setSelectedFestivalForView(null);
                                }}
                                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            {(() => {
                                const allMedia = [...(selectedFestivalForView.festival_images || []), ...(selectedFestivalForView.festival_videos || [])];
                                if (allMedia.length > 0) {
                                    return allMedia.map((imgUrl, index) => {
                                        const isVideo = imgUrl.match(/\.(mp4|webm|avi|mpeg|quicktime|mov)$/i);
                                        return (
                                            <div key={index} className="relative group rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 aspect-[9/16]">
                                                {isVideo ? (
                                                    <video src={imgUrl} controls className="w-full h-full object-cover" />
                                                ) : (
                                                    <img src={imgUrl} alt="festival" className="w-full h-full object-cover" />
                                                )}
                                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                                                    <button
                                                        onClick={() => triggerReplaceImage(imgUrl)}
                                                        className="bg-white/20 hover:bg-white/40 p-2 rounded-full text-white backdrop-blur-sm transition-colors"
                                                        title="Replace"
                                                    >
                                                        <Edit2 size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteImage(imgUrl)}
                                                        className="bg-red-500/80 hover:bg-red-500 p-2 rounded-full text-white backdrop-blur-sm transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    });
                                }
                                return <p className="text-slate-500 dark:text-slate-400 col-span-full">No media available for this festival.</p>;
                            })()}
                        </div>
                    </div>
                </div>
            )}

            <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*,video/*"
                onChange={handleReplaceFileChange}
            />
        </div>
    );
}
